<i18n>
[
    "productTour__proceed--CTA",
    "productTour__complete--CTA",
    "global__gotIt",
    "productTour__boxHeader--title",
    "productTour__boxHeader--subtitle",
    "productTour__closetHeader--title",
    "productTour__closetHeader--subtitle",
    "productTour__sizesFilters--title",
    "productTour__sizesFilters--subtitle",
    "productTour__availabilityToggle--title",
    "productTour__availabilityToggle--subtitle",
    "productTour__closetTile--title",
    "productTour__closetTile--subtitle",
    "productTour__buyHeader--title",
    "productTour__buyHeader--subtitle",
    "productTour__sublistsClosetAndPdp--subtitle",
    "productTour__sublistsClosetAndPdp--cta",
    "productTour__sublistSharing--subtitle",
    "productTour__sublistSharing--cta"
]
</i18n>

<template>
    <div
        v-if="currentTooltipData"
        ref="container"
        :class="{
            'c-product-tour': true,
            'c-product-tour--hidden': !tourStarted,
        }"
    >
        <div
            ref="lightbox"
            :class="{
                'c-product-tour__lightbox': true,
                'c-product-tour__lightbox--offset': !!offset,
                'c-product-tour__lightbox--circular': currentTooltipData.lightboxShape === LIGHTBOX_SHAPES.circle,
            }"
        >
            <BaseTooltip
                ref="tooltip"
                :trigger="'programmatic'"
                :tooltipVerticalMargin="offset"
                :headerText="$t(currentTooltipData.headerSlug || '')"
                :closeText="$t(currentTooltipData.ctaSlug || '')"
                :illustrationSrc="images[currentTooltipData.illustration]"
                :enableClickaway="false"
                @dismiss-cta-triggered="advanceTour"
            >
                <template #launcher>
                    <div class="c-product-tour__tooltip-launcher" @click="advanceTour"></div>
                </template>
                <template #body>
                    <BaseMarkdown
                        :source="$t(currentTooltipData.bodySlug)"
                        class="o-text--caption u-text--gray"
                    />
                </template>
            </BaseTooltip>
        </div>
    </div>
</template>

<script>
import { cloneDeep } from 'lodash-es';
import {
    mapActions,
    mapMutations,
    mapState,
} from 'vuex';

import { GLOBAL_EVENT } from '~coreModules/core/js/store';
import { BROWSER_MODULE_NAME } from '~coreModules/browser/js/browser-store';
import {
    PRODUCT_TOUR_MODULE_NAME,
    MARK_TOUR_COMPLETE,
    SET_ACTIVE_TOUR,
} from '~modules/product-tour/js/product-tour-store';

import { KEYCODES } from '~coreModules/core/js/constants';
import { PRODUCT_TOUR_VIEWED } from '~modules/core/js/global-event-constants';
import {
    PRODUCT_TOUR_NAMES,
    LIGHTBOX_SHAPES,
    PRODUCT_TOUR_DATA,
    PRODUCT_TOUR_ILLUSTRATION_NAMES,
} from '~modules/product-tour/js/product-tour-constants';

import BaseMarkdown from '~coreModules/core/components/ui/BaseMarkdown.vue';
import BaseTooltip from '~coreModules/core/components/ui/tooltips/BaseTooltip.vue';

import ClosetListTourIllustration from '~coreImages/illustration__dancers.png';
import FlowersIllustration from '~coreImages/illustration__flowers.png';
import ClosetIllustration from '~images/illustration__closet.png';
import CatalogTourSizesIllustration from '~images/illustration__sizes.png';
import HomepageTourBoxIllustration from '~images/illustration__box--unlocked.png';
import BoxTourBuyIllustration from '~images/illustration__purchase.png';

export default {
    name: 'ProductTour',
    components: {
        BaseMarkdown,
        BaseTooltip,
    },
    props: {
        productTourData: {
            type: Object,
            default: () => ({}),
        },
        tourName: {
            required: true,
            type: String,
            validator(name) {
                return Object.keys(PRODUCT_TOUR_NAMES).includes(name);
            },
        },
    },
    data() {
        return {
            LIGHTBOX_SHAPES,
            tourStarted: false,
            isTerminated: false,
            targetElement: null,
            tourData: null,
            currentTooltipData: null,
            selectorData: null,
            images: {
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.closetIllustration]:
                    ClosetIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.closetTourIllustration]:
                    FlowersIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.closetListTourIllustration]:
                    ClosetListTourIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.catalogTourSizesIllustration]:
                    CatalogTourSizesIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.catalogTourAvailabilityIllustration]:
                    FlowersIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.homepageTourBoxIllustration]:
                    HomepageTourBoxIllustration,
                [PRODUCT_TOUR_ILLUSTRATION_NAMES.boxTourBuyIllustration]:
                    BoxTourBuyIllustration,
            },
        };
    },
    computed: {
        ...mapState({
            routeIsAutoScrolling: state => state.routeIsAutoScrolling,
        }),
        ...mapState(BROWSER_MODULE_NAME, [
            'clientWidth',
            'clientHeight',
        ]),
        ...mapState(PRODUCT_TOUR_MODULE_NAME, [
            'eligibleTours',
        ]),
        offset() {
            return this.selectorData?.offset || 0;
        },
    },
    watch: {
        '$mediaQueries.currentBreakpoint': function onBreakpointChange() {
            if (this.tourStarted) {
                this.terminateTour();
            }
        },
        clientWidth() {
            this.handleResize();
        },
        clientHeight() {
            this.handleResize();
        },
        routeIsAutoScrolling: {
            handler(isScrolling) {
                if (!isScrolling && !this.tourStarted) {
                    this.tourData = cloneDeep({ ...PRODUCT_TOUR_DATA, ...(this.productTourData ?? {}) }[this.tourName]);
                    this.currentTooltipData = this.tourData.shift();

                    if (this.currentTooltipData) {
                        const self = this;

                        // placing this logic in a timeout as a workaround for iOS Safari firing this logic too early
                        requestAnimationFrame(() => {
                            window.scrollTo(0, 0);

                            requestAnimationFrame(async () => {
                                self.setActiveTour(this.tourName);

                                // awaiting any places in the app which conditionally set the
                                // data-product-tour-lightbox attribute based on the presence of an active tour
                                await self.$nextTick();

                                self.advanceTour(true);

                                // awaiting the child Tooltip updating based on the new tourData
                                await self.$nextTick();

                                if (!this.isTerminated) {
                                    self.positionLightbox();
                                    const { tooltip } = self.$refs;
                                    tooltip.togglePopover(false);
                                    tooltip.$el.focus();
                                    self.tourStarted = true;
                                    self.markTourComplete(this.tourName);
                                }
                            });
                        });
                    }
                }
            },
            immediate: true,
        },
    },
    async mounted() {
        await this.$nextTick();

        document.addEventListener('keydown', this.keydownHandler, true);

        // it is likely that this component will be placed in a different stacking context than the element
        // which should receive a lightbox; this produces z-index issues for the overlay. To prevent this, append
        // the tour's template to div#app (for this pattern, see also: HowToVideo.vue)
        document.querySelector('#app').appendChild(this.$el);
    },
    unmounted() {
        if (!this.isTerminated) {
            this.terminateTour();
        }

        // ensure node is destroyed when component is torn down
        this.$el.remove();
    },
    methods: {
        ...mapActions({
            trackGlobalEvent: GLOBAL_EVENT,
        }),
        ...mapMutations(PRODUCT_TOUR_MODULE_NAME, {
            setActiveTour: SET_ACTIVE_TOUR,
        }),
        ...mapActions(PRODUCT_TOUR_MODULE_NAME, {
            markTourComplete: MARK_TOUR_COMPLETE,
        }),
        advanceTour(isInitialLoad = false) {
            if (!isInitialLoad) {
                this.currentTooltipData = this.tourData.shift();
            }
            let tourIsComplete = !this.currentTooltipData;

            if (!tourIsComplete) {
                this.targetElement = this.getNextLightboxElement();

                if (this.targetElement) {
                    this.positionLightbox();

                    this.trackGlobalEvent({
                        type: PRODUCT_TOUR_VIEWED,
                        data: {
                            type: this.currentTooltipData.type,
                        },
                    });
                } else {
                    tourIsComplete = true;
                }
            }

            if (tourIsComplete) {
                this.terminateTour();
            }
        },
        terminateTour() {
            document.removeEventListener('keydown', this.keydownHandler, true);

            this.targetElement = null;
            this.currentTooltipData = null;
            this.setActiveTour('');
            this.isTerminated = true;
        },
        getNextLightboxElement() {
            while (this.currentTooltipData.lightboxes.length > 0) {
                const attrName = 'data-product-tour-lightbox';
                this.selectorData = this.currentTooltipData.lightboxes.shift();

                // because a single element may be the lightbox target for several different tooltips (e.g. on mobile,
                // the category page filter button is the target of the 'sizes' and 'availability' tooltips), an
                // element may have multiple values attributed to data-product-tour-lightbox; thus we cannot do a simple
                // querySelector lookup based on data-product-tour-lightbox="{{given selector}}"
                const lightboxElement = document.querySelector(`[${attrName}*=${this.selectorData?.selector}]`);

                if (lightboxElement) {
                    this.currentTooltipData.lightboxShape = this.selectorData?.shape;

                    return lightboxElement;
                }

                if (this.currentTooltipData.lightboxes.length === 0 && this.tourData.length > 0) {
                    this.currentTooltipData = this.tourData.shift();
                }
            }

            return null;
        },
        positionLightbox() {
            this.$nextTick(() => {
                const { lightbox } = this.$refs;
                const targetRect = this.targetElement?.getBoundingClientRect();

                const getOffsetSize = size => size + (this.offset * 2);

                lightbox.style.setProperty('--lightboxHeight', `${getOffsetSize(targetRect.height)}px`);
                lightbox.style.setProperty('--lightboxWidth', `${getOffsetSize(targetRect.width)}px`);

                lightbox.style.top = `${targetRect.top - this.offset}px`;
                lightbox.style.left = `${targetRect.left - this.offset}px`;
            });
        },
        handleResize() {
            if (this.currentTooltipData) this.positionLightbox();
        },
        keydownHandler(event) {
            let shouldStopPropagation = true;

            const { keyCode } = event;

            if (keyCode === KEYCODES.escape) {
                this.terminateTour();
            } else if ([KEYCODES.space, KEYCODES.arrowRight].includes(keyCode)) {
                this.advanceTour();
            } else if ([KEYCODES.return, KEYCODES.tab].includes(keyCode)) {
                event.preventDefault();
                this.$refs.tooltip.focusCta();
            } else {
                shouldStopPropagation = false;
            }

            if (shouldStopPropagation) {
                event.stopPropagation();
            }
        },
    },
};
</script>

<style lang="scss">
    .c-product-tour {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        transition: opacity 0.3s ease;

        z-index: 99999;

        &--hidden {
            opacity: 0;
        }

        &__lightbox,
        &__tooltip-launcher {
            height: var(--lightboxHeight);
            width: var(--lightboxWidth);
        }

        &__lightbox {
            position: absolute;
            border-radius: 2px;
            border: 1px solid $nu-secondary;
            box-shadow: 0 0 0 200vw $modal-overlay;

            &--circular {
                border-radius: 50%;
            }

            &--offset {
                border: 1px solid transparent;
            }
        }
    }
</style>
